import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  Alert,
} from '@mui/material';
import { INVERSIONES_SHEET } from '../../../constants';
import { fetchSheetRange } from '../../../services/sheetValues';
import {
  clearInversorSession,
  getInversorSession,
} from '../../../utils/authSession';
import './Dashboard.css';

const INVERSIONES_RANGE = `${INVERSIONES_SHEET}!A1:Z200`;

function headerNorm(h) {
  return String(h ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Índice de columna cuyo encabezado coincide con algún patrón, o -1 */
function findColumnIndex(headers, patterns) {
  return headers.findIndex((h) => {
    const n = headerNorm(h);
    return patterns.some((p) => n.includes(p));
  });
}

function parseAmount(raw) {
  if (raw == null || raw === '') return NaN;
  const s = String(raw).replace(/\s/g, '').replace(/,/g, '');
  const n = parseFloat(s.replace(/[^\d.-]/g, ''));
  return n;
}

function formatCurrencyCell(raw, currencyHint) {
  const n = parseAmount(raw);
  if (Number.isNaN(n)) return raw != null ? String(raw) : '';
  const code = (currencyHint && String(currencyHint).trim()) || 'MXN';
  try {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    }).format(n);
  } catch {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0,
    }).format(n);
  }
}

function emailKey(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase();
}

/** Fila asignada al inversor actual (misma columna que el login: correo en Usuarios B y en esta columna). */
function rowBelongsToUser(row, ownerColIdx, sessionUser) {
  if (ownerColIdx < 0) return true;
  const cell = row[ownerColIdx];
  return emailKey(cell) === emailKey(sessionUser) && emailKey(sessionUser) !== '';
}

function EstadoChip({ value }) {
  const text = value != null ? String(value).trim() : '';
  if (!text) return <span className="dashboard-cell-empty">—</span>;
  const n = headerNorm(text);
  if (n.includes('revision') || n.includes('revisión')) {
    return (
      <Chip
        label={text}
        size="small"
        className="dashboard-chip dashboard-chip--revision"
      />
    );
  }
  if (n.includes('curso') || n.includes('activo')) {
    return (
      <Chip
        label={text}
        size="small"
        className="dashboard-chip dashboard-chip--curso"
      />
    );
  }
  if (
    n.includes('cerrado') ||
    n.includes('finalizado') ||
    n.includes('completado')
  ) {
    return (
      <Chip
        label={text}
        size="small"
        className="dashboard-chip dashboard-chip--cerrado"
      />
    );
  }
  return (
    <Chip label={text} size="small" className="dashboard-chip dashboard-chip--otro" />
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const session = getInversorSession();
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError('');
      try {
        const data = await fetchSheetRange(INVERSIONES_RANGE);
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) {
          setLoadError(
            `No se pudieron cargar los datos. Crea la hoja «${INVERSIONES_SHEET}» en el mismo libro con encabezados en la fila 1.`
          );
          setRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const headers = rows.length > 0 ? rows[0].map((h, i) => (h != null && String(h).trim() !== '' ? String(h).trim() : `Col ${i + 1}`)) : [];
  const bodyRows = rows.length > 1 ? rows.slice(1) : [];

  const columnHints = useMemo(() => {
    const montoIdx = findColumnIndex(headers, [
      'monto invertido',
      'monto',
      'importe',
      'inversion',
      'capital',
    ]);
    const monedaIdx = findColumnIndex(headers, ['moneda']);
    const estadoIdx = findColumnIndex(headers, ['estado']);
    const usuarioInvirtioIdx = findColumnIndex(headers, [
      'usuario invirtio',
      'usuario que invirtio',
      'correo inversor',
      'email inversor',
      'correo del inversor',
    ]);
    return { montoIdx, monedaIdx, estadoIdx, usuarioInvirtioIdx };
  }, [headers]);

  const filteredBodyRows = useMemo(() => {
    const { usuarioInvirtioIdx } = columnHints;
    const user = session?.username;
    if (usuarioInvirtioIdx < 0) return bodyRows;
    return bodyRows.filter((row) => rowBelongsToUser(row, usuarioInvirtioIdx, user));
  }, [bodyRows, columnHints, session?.username]);

  const renderCell = (row, ci) => {
    const raw = row[ci];
    const { montoIdx, monedaIdx, estadoIdx } = columnHints;
    if (ci === estadoIdx) {
      return <EstadoChip value={raw} />;
    }
    if (ci === montoIdx && montoIdx >= 0) {
      const moneda =
        monedaIdx >= 0 && row[monedaIdx] != null ? row[monedaIdx] : 'MXN';
      return (
        <span className="dashboard-monto">{formatCurrencyCell(raw, moneda)}</span>
      );
    }
    return raw != null ? String(raw) : '';
  };

  const logout = () => {
    clearInversorSession();
    navigate('/login', { replace: true });
  };

  return (
    <Box className="dashboard-root">
      <AppBar position="static" className="dashboard-appbar" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Panel de inversiones
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>
            {session?.username}
          </Typography>
          <Button color="inherit" onClick={logout}>
            Salir
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" className="dashboard-content">
        <Box className="dashboard-intro">
          <Typography variant="h5" component="h1" gutterBottom className="dashboard-title">
            Inmuebles en curso
          </Typography>
          <Typography variant="body2" color="text.secondary" className="dashboard-subtitle">
            {columnHints.usuarioInvirtioIdx >= 0
              ? 'Solo se listan tus inversiones (mismo correo que usaste al iniciar sesión).'
              : 'Resumen de proyectos e importes según los datos del libro compartido.'}
          </Typography>
        </Box>

        {loadError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        ) : null}

        {!loadError && columnHints.usuarioInvirtioIdx < 0 && bodyRows.length > 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Añade una columna «Usuario Invirtió» (o «Correo inversor») con el correo del inversor en
            cada fila para que cada usuario solo vea sus propias inversiones.
          </Alert>
        ) : null}

        {!loadError && bodyRows.length === 0 && rows.length <= 1 ? (
          <Paper className="dashboard-empty">
            <Typography variant="body1">
              Aún no hay filas de datos bajo los encabezados en la hoja «{INVERSIONES_SHEET}».
            </Typography>
          </Paper>
        ) : null}

        {!loadError &&
        bodyRows.length > 0 &&
        columnHints.usuarioInvirtioIdx >= 0 &&
        filteredBodyRows.length === 0 ? (
          <Paper className="dashboard-empty">
            <Typography variant="body1" gutterBottom>
              No hay inversiones asociadas a tu usuario.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              El correo en la columna «Usuario Invirtió» debe coincidir exactamente con el usuario con
              el que iniciaste sesión ({session?.username || '—'}).
            </Typography>
          </Paper>
        ) : null}

        {headers.length > 0 && filteredBodyRows.length > 0 ? (
          <TableContainer
            component={Paper}
            className="dashboard-table-wrap"
            elevation={0}
          >
            <Table stickyHeader className="dashboard-table" size="medium">
              <TableHead>
                <TableRow className="dashboard-thead-row">
                  {headers.map((h, i) => (
                    <TableCell key={i} className="dashboard-th">
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBodyRows.map((row, ri) => (
                  <TableRow
                    key={ri}
                    hover
                    className={`dashboard-tr${ri % 2 === 1 ? ' dashboard-tr--alt' : ''}`}
                  >
                    {headers.map((_, ci) => (
                      <TableCell key={ci} className="dashboard-td">
                        {renderCell(row, ci)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Container>
    </Box>
  );
}
